// src/pages/forge/SquadDashboard.tsx - OPTIMIZED VERSION
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Target,
  Users,
  FileText,
  Calendar,
  ChevronRight,
  Award,
  MessageSquare,
  Upload,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  MapPin,
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
  Video,
  Plus,
} from 'lucide-react';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { supabase } from '@/lib/supabaseClient';
import { ProblemBankEntry } from '@/types/problemBank';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Helper functions remain the same...
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleBadgeColor = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'market_analyst': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'product_lead': return 'bg-green-100 text-green-800 border-green-200';
    case 'finance_lead': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getDisplayRole = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'Project Lead';
    case 'market_analyst': return 'Market Analyst';
    case 'product_lead': return 'Product Lead';
    case 'finance_lead': return 'Finance Lead';
    default: return role || 'Team Member';
  }
};

interface Meeting {
  id: string;
  title: string;
  description: string;
  meeting_type: 'internal' | 'client' | 'mentor' | 'review';
  scheduled_for: string;
  duration_minutes: number;
  location: string;
  meeting_link?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  created_by: string;
  created_by_name: string;
  squad_id: string;
  created_at: string;
}

function parseProblemData(problem: any): ProblemBankEntry {
  return {
    ...problem,
    id: problem.id,
    problemId: problem.problem_id,
    title: problem.title,
    status: problem.status,
    dateDiscovered: problem.date_discovered,
    source: problem.source,
    category: problem.category || [],
    industrySector: problem.industry_sector,
    anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor || '{}') : problem.anchor,
    quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification || '{}') : problem.quantification,
    rootCauseAnalysis: typeof problem.root_cause_analysis === 'string' ? JSON.parse(problem.root_cause_analysis || '{}') : problem.rootCauseAnalysis,
    solutionPotential: typeof problem.solution_potential === 'string' ? JSON.parse(problem.solution_potential || '{}') : problem.solutionPotential,
    verification: typeof problem.verification === 'string' ? JSON.parse(problem.verification || '{}') : problem.verification,
    priorityScore: problem.priority_score,
    assignment: problem.assignment,
    outcome: problem.outcome,
    createdAt: problem.created_at,
    updatedAt: problem.updated_at
  };
}

export default function SquadDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { squadUser, isAuthenticated } = useSquadAuth();
  
  // Separate loading states for different sections
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingProblems, setIsLoadingProblems] = useState(false);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  
  const [availableProblems, setAvailableProblems] = useState<ProblemBankEntry[]>([]);
  const [assignedProblems, setAssignedProblems] = useState<ProblemBankEntry[]>([]);
  const [squadMembers, setSquadMembers] = useState<any[]>([]);
  const [loisCount, setLoisCount] = useState(0);
  const [interviewsCount, setInterviewsCount] = useState(0);
  const [memberKPIProgress, setMemberKPIProgress] = useState<Record<string, { completed: number; total: number }>>({});
  const [upcomingMeetings, setUpcomingMeetings] = useState<Meeting[]>([]);
  
  // Cache refs to prevent unnecessary updates
  const dataVersionRef = useRef({ members: 0, problems: 0, meetings: 0 });
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    meeting_type: 'internal' as const,
    scheduled_for: '',
    duration_minutes: 60,
    location: '',
    meeting_link: ''
  });
  const [isSchedulingMeeting, setIsSchedulingMeeting] = useState(false);
  
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const subscriptionsRef = useRef<any[]>([]);
  const isMountedRef = useRef(true);

  const [forgePhase] = useState({
    startDate: '2024-01-15',
    endDate: '2024-04-14',
    currentDay: 32,
    totalDays: 90,
    progress: 35
  });

  // Load squad members only
  const loadSquadMembers = useCallback(async () => {
    if (!squadUser?.squadId || !isMountedRef.current) return;
    
    setIsLoadingMembers(true);
    try {
      const { data: members, error } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', squadUser.squadId);

      if (!error && members && isMountedRef.current) {
        setSquadMembers(members);
        await loadMemberKPIProgress(members);
        dataVersionRef.current.members++;
      }
    } catch (error) {
      console.error('Error loading squad members:', error);
    } finally {
      if (isMountedRef.current) setIsLoadingMembers(false);
    }
  }, [squadUser?.squadId]);

  // Load problems only
  const loadProblems = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setIsLoadingProblems(true);
    try {
      const [problemsResult, assignedResult] = await Promise.all([
        supabase.from('problem_bank').select('*').eq('status', 'verified').order('created_at', { ascending: false }),
        supabase.from('problem_bank').select('*').eq('status', 'assigned')
      ]);

      if (!problemsResult.error && problemsResult.data && isMountedRef.current) {
        setAvailableProblems(problemsResult.data.map(parseProblemData));
      }

      if (!assignedResult.error && assignedResult.data && isMountedRef.current && squadUser?.squadId) {
        const squadAssigned = assignedResult.data.filter(problem => 
          problem.assignment && JSON.parse(problem.assignment || '{}').squadId === squadUser.squadId
        );
        setAssignedProblems(squadAssigned.map(parseProblemData));
        dataVersionRef.current.problems++;
      }
    } catch (error) {
      console.error('Error loading problems:', error);
    } finally {
      if (isMountedRef.current) setIsLoadingProblems(false);
    }
  }, [squadUser?.squadId]);

  // Load meetings only
  const loadMeetings = useCallback(async () => {
    if (!squadUser?.squadId || !isMountedRef.current) return;
    
    setIsLoadingMeetings(true);
    try {
      const [loisResult, meetingsResult, squadMeetingsResult] = await Promise.all([
        supabase.from('letters_of_intent').select('id').eq('squad_id', squadUser.squadId),
        supabase.from('customer_meetings').select('id').eq('squad_id', squadUser.squadId),
        supabase.from('squad_meetings')
          .select('*')
          .eq('squad_id', squadUser.squadId)
          .eq('status', 'scheduled')
          .gte('scheduled_for', new Date().toISOString())
          .order('scheduled_for', { ascending: true })
          .limit(5)
      ]);

      if (!loisResult.error && isMountedRef.current) {
        setLoisCount(loisResult.data?.length || 0);
      }
      if (!meetingsResult.error && isMountedRef.current) {
        setInterviewsCount(meetingsResult.data?.length || 0);
      }
      if (!squadMeetingsResult.error && squadMeetingsResult.data && isMountedRef.current) {
        setUpcomingMeetings(squadMeetingsResult.data);
        dataVersionRef.current.meetings++;
      }
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      if (isMountedRef.current) setIsLoadingMeetings(false);
    }
  }, [squadUser?.squadId]);

  const loadMemberKPIProgress = async (members: any[]) => {
    const progress: Record<string, { completed: number; total: number }> = {};
    
    // Use Promise.all for parallel loading
    await Promise.all(members.map(async (member) => {
      const [kpiDefs, completedKPIs] = await Promise.all([
        supabase.from('squad_kpi_definitions').select('id').eq('role', member.role),
        supabase.from('squad_member_kpis').select('status').eq('squad_member_id', member.id).in('status', ['approved', 'completed'])
      ]);
      progress[member.id] = {
        completed: completedKPIs.data?.length || 0,
        total: kpiDefs.data?.length || 0
      };
    }));
    
    if (isMountedRef.current) {
      setMemberKPIProgress(progress);
    }
  };

  // Debounced refresh - only reload what changed
  const debouncedRefresh = useCallback((type: 'members' | 'problems' | 'meetings' | 'all') => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      if (type === 'members' || type === 'all') loadSquadMembers();
      if (type === 'problems' || type === 'all') loadProblems();
      if (type === 'meetings' || type === 'all') loadMeetings();
      setLastUpdated(new Date());
    }, 300); // 300ms debounce
  }, [loadSquadMembers, loadProblems, loadMeetings]);

  const handleScheduleMeeting = async () => {
    if (!newMeeting.title || !newMeeting.scheduled_for) {
      toast({
        title: "Missing Information",
        description: "Please provide a title and date/time for the meeting.",
        variant: "destructive"
      });
      return;
    }

    setIsSchedulingMeeting(true);
    try {
      const { data, error } = await supabase
        .from('squad_meetings')
        .insert({
          title: newMeeting.title,
          description: newMeeting.description,
          meeting_type: newMeeting.meeting_type,
          scheduled_for: newMeeting.scheduled_for,
          duration_minutes: newMeeting.duration_minutes,
          location: newMeeting.location,
          meeting_link: newMeeting.meeting_link,
          status: 'scheduled',
          created_by: squadUser?.memberId,
          created_by_name: squadUser?.memberName,
          squad_id: squadUser?.squadId
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Meeting Scheduled",
        description: `${newMeeting.title} has been scheduled for ${new Date(newMeeting.scheduled_for).toLocaleString()}`,
      });

      setNewMeeting({
        title: '',
        description: '',
        meeting_type: 'internal',
        scheduled_for: '',
        duration_minutes: 60,
        location: '',
        meeting_link: ''
      });
      setMeetingDialogOpen(false);
      
      // Only reload meetings, not everything
      loadMeetings();
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: "Error",
        description: "Failed to schedule meeting. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSchedulingMeeting(false);
    }
  };

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([loadSquadMembers(), loadProblems(), loadMeetings()]);
      toast({
        title: "Data Refreshed",
        description: "Your dashboard has been updated.",
      });
    } catch (error) {
      console.error('Refresh failed:', error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [loadSquadMembers, loadProblems, loadMeetings, toast]);

  // Setup real-time subscriptions with selective updates
  useEffect(() => {
    if (!isAuthenticated || !squadUser?.squadId) return;

    isMountedRef.current = true;
    
    // Initial load - show loading only once
    Promise.all([loadSquadMembers(), loadProblems(), loadMeetings()]).finally(() => {
      if (isMountedRef.current) setIsInitialLoad(false);
    });

    const setupSubscriptions = () => {
      subscriptionsRef.current.forEach(sub => {
        try { sub.unsubscribe(); } catch (e) {}
      });
      subscriptionsRef.current = [];

      // Problem Bank subscription - only reload problems
      const problemBankSub = supabase
        .channel('problem_bank_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'problem_bank' }, () => {
          debouncedRefresh('problems');
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') setIsRealtimeConnected(true);
          else if (status === 'CHANNEL_ERROR') setIsRealtimeConnected(false);
        });

      // Squad members subscription
      const squadMembersSub = supabase
        .channel('squad_members_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_members', filter: `squad_id=eq.${squadUser.squadId}` }, () => {
          debouncedRefresh('members');
        })
        .subscribe();

      // Squad meetings subscription
      const squadMeetingsSub = supabase
        .channel('squad_meetings_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_meetings', filter: `squad_id=eq.${squadUser.squadId}` }, () => {
          debouncedRefresh('meetings');
        })
        .subscribe();

      subscriptionsRef.current = [problemBankSub, squadMembersSub, squadMeetingsSub];
    };

    setupSubscriptions();

    return () => {
      isMountedRef.current = false;
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
      subscriptionsRef.current.forEach(sub => {
        try { sub.unsubscribe(); } catch (e) {}
      });
    };
  }, [isAuthenticated, squadUser?.squadId, loadSquadMembers, loadProblems, loadMeetings, debouncedRefresh]);

  const getStatusBadgeClass = (status: string) => {
    switch(status) {
      case 'verified': return 'bg-green-100 text-green-700';
      case 'assigned': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getMeetingTypeIcon = (type: string) => {
    switch(type) {
      case 'internal': return <Users className="h-4 w-4" />;
      case 'client': return <Users className="h-4 w-4" />;
      case 'mentor': return <Award className="h-4 w-4" />;
      case 'review': return <CheckCircle2 className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  const getMeetingTypeColor = (type: string) => {
    switch(type) {
      case 'internal': return 'bg-blue-100 text-blue-700';
      case 'client': return 'bg-green-100 text-green-700';
      case 'mentor': return 'bg-purple-100 text-purple-700';
      case 'review': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const totalKPIsAll = useMemo(() => {
    return Object.values(memberKPIProgress).reduce((sum, m) => sum + m.total, 0);
  }, [memberKPIProgress]);
  
  const completedKPIsAll = useMemo(() => {
    return Object.values(memberKPIProgress).reduce((sum, m) => sum + m.completed, 0);
  }, [memberKPIProgress]);

  const displayProblems = useMemo(() => availableProblems.slice(0, 3), [availableProblems]);

  // Show initial loading only once
  if (isInitialLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const getMemberFirstName = () => {
    if (squadUser?.memberName) return squadUser.memberName.split(' ')[0];
    if (squadUser?.name) return squadUser.name.split(' ')[0];
    return 'Team';
  };

  // Determine if any section is loading (for skeleton UI)
  const showSkeleton = isLoadingMembers || isLoadingProblems || isLoadingMeetings;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Welcome back, {getMemberFirstName()}!
            </h1>
            <p className="text-muted-foreground">Day {forgePhase.currentDay} of 90 • {forgePhase.progress}% through The Forge</p>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-xs text-muted-foreground">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
              <div className="flex items-center gap-1">
                {isRealtimeConnected ? (
                  <>
                    <Wifi className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3 text-red-500" />
                    <span className="text-xs text-red-600">Reconnecting...</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
        <Progress value={forgePhase.progress} className="h-2 mt-2" />
      </div>

      {/* Stats Cards - Always visible, show loading overlay if needed */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="p-1.5 bg-primary/10 rounded-lg mr-3">
              <Target className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Problems</p>
              <p className="text-xl font-bold text-foreground">{availableProblems.length + assignedProblems.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="p-1.5 bg-primary/10 rounded-lg mr-3">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Interviews</p>
              <p className="text-xl font-bold text-foreground">{interviewsCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="p-1.5 bg-primary/10 rounded-lg mr-3">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">KPIs</p>
              <p className="text-xl font-bold text-foreground">{completedKPIsAll}/{totalKPIsAll}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center">
            <div className="p-1.5 bg-primary/10 rounded-lg mr-3">
              <Award className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">LOIs</p>
              <p className="text-xl font-bold text-foreground">{loisCount}/3</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          {/* Problem Bank Section - Skeleton while loading */}
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Problem Bank</CardTitle>
                  <CardDescription className="text-xs">Browse problems to tackle</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-primary" asChild>
                  <Link to="/forge/problem-bank">
                    View All <ChevronRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              {isLoadingProblems ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg animate-pulse">
                      <div className="flex-1">
                        <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                      <div className="h-7 w-16 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : displayProblems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No verified problems available yet.</p>
                  <p className="text-xs mt-1">Check back later when analysts verify new problems.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {displayProblems.map((problem) => (
                    <div 
                      key={problem.id} 
                      className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition cursor-pointer"
                      onClick={() => navigate(`/forge/problem/${problem.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-foreground truncate">{problem.title}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                            {problem.industrySector?.replace('_', ' ') || 'General'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] px-1 py-0 h-4 ${getStatusBadgeClass(problem.status)}`}>
                            {problem.status}
                          </Badge>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 px-2 ml-2">View</Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Problems Section */}
          {assignedProblems.length > 0 && (
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="text-lg">Your Assigned Problems</CardTitle>
                <CardDescription className="text-xs">Problems your squad is working on</CardDescription>
              </CardHeader>
              <CardContent className="pt-0 pb-4">
                <div className="space-y-2">
                  {assignedProblems.slice(0, 3).map((problem) => (
                    <div 
                      key={problem.id} 
                      className="flex items-center justify-between p-2 bg-purple-50 rounded-lg hover:bg-purple-100 transition cursor-pointer"
                      onClick={() => navigate(`/forge/problem/${problem.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-foreground truncate">{problem.title}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-purple-100">
                            Assigned
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground">
                            Loss: KES {(problem.quantification?.totalLossPerYear / 1000000 || 0).toFixed(1)}M/year
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 px-2 ml-2">View</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Meetings Section */}
          <Card>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Meetings
                  </CardTitle>
                  <CardDescription className="text-xs">Scheduled squad meetings and events</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setMeetingDialogOpen(true)} className="gap-1">
                  <Plus className="h-3 w-3" />
                  Schedule Meeting
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              {isLoadingMeetings ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="p-3 border rounded-lg animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                      <div className="h-3 bg-muted rounded w-full mb-1"></div>
                      <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : upcomingMeetings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No upcoming meetings</p>
                  <p className="text-xs mt-1">Schedule a meeting to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingMeetings.map((meeting) => (
                    <div key={meeting.id} className="p-3 border rounded-lg hover:bg-muted/50 transition cursor-pointer">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${getMeetingTypeColor(meeting.meeting_type)} text-[10px] px-1.5 py-0 h-5`}>
                              <span className="flex items-center gap-1">
                                {getMeetingTypeIcon(meeting.meeting_type)}
                                {meeting.meeting_type.charAt(0).toUpperCase() + meeting.meeting_type.slice(1)}
                              </span>
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(meeting.scheduled_for).toLocaleDateString()} at {new Date(meeting.scheduled_for).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <h4 className="font-medium text-sm">{meeting.title}</h4>
                          {meeting.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{meeting.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {meeting.duration_minutes} min
                            </span>
                            {meeting.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {meeting.location}
                              </span>
                            )}
                            {meeting.meeting_link && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Video className="h-3 w-3" />
                                Video Link
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Individual KPIs */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-lg">Individual KPIs</CardTitle>
              <CardDescription className="text-xs">Track each member's progress</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              {isLoadingMembers ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-1">
                        <div className="h-3 bg-muted rounded w-24"></div>
                        <div className="h-3 bg-muted rounded w-12"></div>
                      </div>
                      <div className="h-1.5 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : squadMembers.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No squad members found
                </div>
              ) : (
                <div className="space-y-3">
                  {squadMembers.map((member) => {
                    const progress = memberKPIProgress[member.id] || { completed: 0, total: 0 };
                    const percentComplete = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
                    return (
                      <div key={member.id}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-foreground font-medium">{getDisplayRole(member.role)}</span>
                          <span className="text-muted-foreground">{progress.completed}/{progress.total}</span>
                        </div>
                        <Progress value={percentComplete} className="h-1.5" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Rest remains the same but with skeleton support */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-lg">Squad Members</CardTitle>
              <CardDescription className="text-xs">
                {squadMembers.length} team members • {squadUser?.squadName || 'Your Squad'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              {isLoadingMembers ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-start gap-3 animate-pulse">
                      <div className="h-14 w-14 rounded-full bg-muted"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-24 mb-1"></div>
                        <div className="h-2 bg-muted rounded w-full"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : squadMembers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No squad members found</p>
                  <p className="text-xs mt-1">Invite team members to join your squad</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {squadMembers.map((member) => {
                    const progress = memberKPIProgress[member.id] || { completed: 0, total: 0 };
                    const percentComplete = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
                    
                    return (
                      <Card 
                        key={member.id} 
                        className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden border-0 shadow-none"
                        onClick={() => navigate(`/forge/squad/${member.id}`)}
                      >
                        <CardContent className="p-0">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="h-14 w-14 rounded-full overflow-hidden border-2 border-white shadow-md bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                <div className={`h-full w-full flex items-center justify-center text-lg font-bold ${getRoleBadgeColor(member.role)}`}>
                                  {getInitials(member.full_name)}
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h3 className="text-base font-semibold text-foreground">{member.full_name}</h3>
                                  <p className="text-xs text-muted-foreground mt-0.5">{member.year_of_study || 'N/A'} • {member.course || 'N/A'}</p>
                                </div>
                                <Badge className={getRoleBadgeColor(member.role)}>
                                  {getDisplayRole(member.role)}
                                </Badge>
                              </div>
                              <div className="space-y-1 mt-2 text-xs">
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <Mail className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{member.email}</span>
                                </div>
                                {member.phone && (
                                  <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <Phone className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{member.phone}</span>
                                  </div>
                                )}
                                {member.location && (
                                  <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <MapPin className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{member.location}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">KPI Progress</span>
                                  <span className="text-foreground">{progress.completed}/{progress.total}</span>
                                </div>
                                <Progress value={percentComplete} className="h-1.5" />
                              </div>
                              <div className="mt-2">
                                <Badge variant="outline" className={member.assessment_completed ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}>
                                  {member.assessment_completed ? 'Assessment Complete' : 'Assessment Pending'}
                                </Badge>
                              </div>
                              <div className="mt-2 text-right">
                                <span className="text-xs text-primary group-hover:text-primary/80 transition-colors inline-flex items-center gap-1">
                                  View full profile
                                  <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {squadMembers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-primary/5 rounded-lg">
                      <p className="text-2xl font-bold text-primary">
                        {totalKPIsAll > 0 ? Math.round((completedKPIsAll / totalKPIsAll) * 100) : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">Team Completion</p>
                    </div>
                    <div className="text-center p-3 bg-primary/5 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{completedKPIsAll}</p>
                      <p className="text-xs text-muted-foreground">KPIs Completed</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader className="py-4">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-4 space-y-1.5">
              <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" asChild>
                <Link to="/forge/problem-bank">
                  <Target className="mr-2 h-3 w-3" /> Browse Problems
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                <Upload className="mr-2 h-3 w-3" /> Submit KPI
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8" onClick={() => setMeetingDialogOpen(true)}>
                <Calendar className="mr-2 h-3 w-3" /> Schedule Meeting
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                <MessageSquare className="mr-2 h-3 w-3" /> Message Squad
              </Button>
            </CardContent>
          </Card>

          {/* Forge Timeline */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-lg">Timeline</CardTitle>
              <CardDescription className="text-xs">90-day journey</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              <div className="space-y-1.5">
                <div className="flex justify-between items-center p-1.5 text-xs">
                  <span className="text-muted-foreground">Formation</span>
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                </div>
                <div className="flex justify-between items-center p-1.5 text-xs">
                  <span className="text-muted-foreground">Problem Selection</span>
                  <Clock className="h-3 w-3 text-yellow-500" />
                </div>
                <div className="flex justify-between items-center p-1.5 text-xs">
                  <span className="text-muted-foreground">Solution Dev</span>
                  <Clock className="h-3 w-3 text-gray-300" />
                </div>
                <div className="flex justify-between items-center p-1.5 text-xs">
                  <span className="text-muted-foreground">Validation</span>
                  <Clock className="h-3 w-3 text-gray-300" />
                </div>
                <div className="flex justify-between items-center p-1.5 text-xs">
                  <span className="text-muted-foreground">Graduation</span>
                  <Clock className="h-3 w-3 text-gray-300" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Schedule Meeting Dialog - FIXED SCROLLING */}
      <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Schedule a Meeting</DialogTitle>
            <DialogDescription>
              Schedule a team meeting, client call, or mentor session.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Meeting Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Weekly Sprint Planning"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="meeting_type">Meeting Type</Label>
              <Select 
                value={newMeeting.meeting_type} 
                onValueChange={(value: any) => setNewMeeting({ ...newMeeting, meeting_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select meeting type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Team Meeting</SelectItem>
                  <SelectItem value="client">Client Meeting</SelectItem>
                  <SelectItem value="mentor">Mentor Session</SelectItem>
                  <SelectItem value="review">Progress Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="scheduled_for">Date & Time *</Label>
              <Input
                id="scheduled_for"
                type="datetime-local"
                value={newMeeting.scheduled_for}
                onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_for: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="duration_minutes">Duration (minutes)</Label>
              <Select 
                value={newMeeting.duration_minutes.toString()} 
                onValueChange={(value) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="90">90 minutes</SelectItem>
                  <SelectItem value="120">120 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="e.g., Conference Room A, Zoom, Google Meet"
                value={newMeeting.location}
                onChange={(e) => setNewMeeting({ ...newMeeting, location: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="meeting_link">Meeting Link (Optional)</Label>
              <Input
                id="meeting_link"
                placeholder="https://zoom.us/..."
                value={newMeeting.meeting_link}
                onChange={(e) => setNewMeeting({ ...newMeeting, meeting_link: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description / Agenda</Label>
              <Textarea
                id="description"
                placeholder="What will be discussed in this meeting?"
                rows={3}
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setMeetingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleScheduleMeeting} disabled={isSchedulingMeeting}>
              {isSchedulingMeeting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Scheduling...</>
              ) : (
                <><Calendar className="mr-2 h-4 w-4" />Schedule Meeting</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}